import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import { setSessionWithExpiry, getValidSession } from '../helpers/sessionUtils';

const OTP_VALID_SECONDS = 5 * 60;

// Simple message display
const FormMessageDisplay = ({ message, type }) => {
  if (!message) return null;
  const messageClass =
    type === 'error' ? 'form-message-error' : type === 'success' ? 'form-message-success' : 'form-message-info';
  return (
    <div className={`form-message-display ${messageClass} mb-3`} role="alert">
      {message}
    </div>
  );
};

/**
 * LoginPopup
 * - Accepts optional props:
 *    visible: boolean          -> if provided, controls visibility from parent
 *    onClose: function         -> called when popup is closed (so parent can update its state)
 *    onLoginSuccess: function  -> called with user data after successful login (skips page reload)
 */
function LoginPopup({ visible, isOpen, onClose, onLoginSuccess, autoShow = false }) {
  console.log("Rendering LoginPopup component. Props:", { visible, isOpen, autoShow });
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

  const [phone, setPhone] = useState('');
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [remainingTime, setRemainingTime] = useState(OTP_VALID_SECONDS);
  const timerRef = useRef(null);
  const verifyInFlightRef = useRef(false);
  const [isOtpFormValid, setIsOtpFormValid] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [userIdToVerify, setUserIdToVerify] = useState('');
  const [formMessage, setFormMessage] = useState({ text: '', type: '' });

  // Determine whether popup should be visible.
  // We check both 'visible' and 'isOpen' props for compatibility with various pages.
  const isVisible = visible === true || isOpen === true || showPopup === true;

  // service type and inject stylesheet (same as before)
  useEffect(() => {
    if (
      location.pathname === '/holidays' ||
      location.pathname === '/holidays-list' ||
      location.pathname === '/holiday-details'
    ) {
      setServiceType('holidays');
    } else if (
      location.pathname === '/hotels' ||
      location.pathname === '/hotel-list' ||
      location.pathname === '/hotel-details'
    ) {
      setServiceType('hotels');
    } else if (location.pathname === '/cabs') {
      setServiceType('cabs');
    } else if (location.pathname === '/tirthyatra' || location.pathname === '/yatra-list' || location.pathname.startsWith('/yatra-details')) {
      setServiceType('tirth-yatra');
    } else if (location.pathname === '/medicaltourism') {
      setServiceType('medical-tourism');
    }

    const themeStylesheet = document.getElementById('loginpopup-css');
    if (!themeStylesheet) {
      const link = document.createElement('link');
      link.id = 'loginpopup-css';
      link.rel = 'stylesheet';
      link.href = '/assets/css/LoginPopup.css';
      document.head.appendChild(link);
    }
  }, [location]);

  // Global auto-show logic with persistent timer
  useEffect(() => {
    console.log("LoginPopup mounted/updated. autoShow:", autoShow, "Path:", location.pathname);

    // Only run auto-show logic if enabled via prop
    if (!autoShow) return;

    let timeoutId;
    const validUserSession = getValidSession();
    console.log("Valid session found?", !!validUserSession);

    // Check for explicit public paths and detail patterns
    const isPublicPath = [
      '/', '/holidays', '/hotels', '/cabs', '/tirthyatra', '/medicaltourism',
      '/holidays-list', '/hotel-list', '/cabs/search', '/search', '/yatra-list'
    ].includes(location.pathname) ||
      location.pathname.startsWith('/holiday-details/') ||
      location.pathname.startsWith('/hotel-details/') ||
      location.pathname.startsWith('/medical-details/') ||
      location.pathname.startsWith('/yatra-details/');

    const shouldShowOnPath = isPublicPath;
    console.log("Should show on this path?", shouldShowOnPath);

    // We only want to auto-show if not logged in and on a public path
    if (shouldShowOnPath && !validUserSession) {
      // Use sessionStorage to track the 5-second mark across page navigations
      let sessionStart = sessionStorage.getItem('loginPopupFirstVisit');
      if (!sessionStart) {
        sessionStart = Date.now().toString();
        sessionStorage.setItem('loginPopupFirstVisit', sessionStart);
        console.log("Started new visit timer at:", sessionStart);
      }

      const elapsed = Date.now() - parseInt(sessionStart);
      const remaining = Math.max(0, 5000 - elapsed);
      console.log(`Elapsed: ${elapsed}ms, Remaining: ${remaining}ms`);

      timeoutId = setTimeout(() => {
        console.log("Timer finished. Re-checking session...");
        // Re-check session right before showing
        if (!getValidSession()) {
          console.log("No session found. SHOWING POPUP.");
          setShowPopup(true);
        } else {
          console.log("Session found at last second. Aborting.");
        }
      }, remaining);
    } else if (!shouldShowOnPath) {
      // Hide on non-public paths (auth, admin)
      setShowPopup(false);
    }

    return () => {
      console.log("Cleaning up timer for path:", location.pathname);
      clearTimeout(timeoutId);
    };
  }, [location.pathname, autoShow]);

  // Listen for global trigger events
  useEffect(() => {
    const handleTrigger = (event) => {
      if (event.detail && event.detail.show !== undefined) {
        setShowPopup(event.detail.show);
      } else {
        setShowPopup(true);
      }
    };

    window.addEventListener('toggleLoginPopup', handleTrigger);
    return () => window.removeEventListener('toggleLoginPopup', handleTrigger);
  }, []);

  // form validation effects
  useEffect(() => {
    setIsFormValid(name.trim() !== '' && phone.trim() !== '' && phone.length > 5);
  }, [name, phone]);

  useEffect(() => {
    if (otpSent && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (remainingTime === 0) {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [otpSent, remainingTime]);

  useEffect(() => {
    setIsOtpFormValid(otpDigits.every((digit) => digit !== ''));
  }, [otpDigits]);

  // helper to close popup and also notify parent if provided
  const closePopup = () => {
    setShowPopup(false);
    setOtpSent(false);
    setOtpDigits(['', '', '', '', '', '']);
    setRemainingTime(OTP_VALID_SECONDS);
    setIsVerifyingOtp(false);
    verifyInFlightRef.current = false;
    if (typeof onClose === 'function') onClose();
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      alert('Please fill in your name and a valid mobile number.');
      return;
    }

    const mobileNumber = phone;
    const userDataPayload = {
      name: name,
      phone: mobileNumber,
      email: email || null,
      role_id: 3,
    };

    try {
      const registerAndSendOtpResponse = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userDataPayload),
      });

      const responseData = await registerAndSendOtpResponse.json();

      if (registerAndSendOtpResponse.ok && responseData.status === 'otp_sent') {
        setOtpSent(true);
        setUserIdToVerify(responseData.user.id);
        setRemainingTime(OTP_VALID_SECONDS);
        setIsVerifyingOtp(false);
        verifyInFlightRef.current = false;
        console.log('Registration/Login initiated, OTP Sent. UserID:', responseData.user.id);
      } else {
        alert(`Failed: ${responseData.message || 'An error occurred. Please try again.'}`);
      }
    } catch (error) {
      console.error('Error during registration/OTP sending request:', error);
      alert('Failed to process request. Please check your network and try again.');
    }
  };

  const handleOtpSubmit = async (e, otpOverride = null) => {
    if (e) e.preventDefault();
    const enteredOtp = otpOverride || otpDigits.join('');

    if (verifyInFlightRef.current) return;

    if (remainingTime <= 0) {
      alert('OTP has expired. Please request a new OTP.');
      return;
    }

    if (!userIdToVerify) {
      alert('User session error. Please try sending OTP again by re-entering your mobile number.');
      setOtpSent(false);
      return;
    }

    verifyInFlightRef.current = true;
    setIsVerifyingOtp(true);

    try {
      const loginVerifyResponse = await fetch(`${API_BASE_URL}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userIdToVerify, otp: enteredOtp }),
      });

      const loginData = await loginVerifyResponse.json();

      if (loginVerifyResponse.ok && loginData.status === 'otp_verified_login_successful') {
        const userDataToStore = loginData.user;
        const token = loginData.token;

        if (userDataToStore && userDataToStore.id && token) {
          // Store session appropriately for vendor or visitor
          if (loginData.isVendor) {
            localStorage.setItem('userInfo', JSON.stringify(userDataToStore));
            localStorage.removeItem('visitorUser');
          } else {
            localStorage.setItem('visitorUser', JSON.stringify(userDataToStore));
            localStorage.removeItem('userInfo');
          }

          localStorage.setItem('authToken', token);
          localStorage.setItem('token', token);

          setSessionWithExpiry({ user: userDataToStore, token: token });
          closePopup();
          if (typeof onLoginSuccess === 'function') {
            onLoginSuccess(userDataToStore);
          } else {
            window.location.reload();
          }
        } else {
          console.error('Login data incomplete:', loginData);
          alert('Login succeeded but user data or token seems incomplete.');
        }
      } else {
        alert(`OTP Verification Failed: ${loginData.message || 'Incorrect OTP or error.'}`);
      }
    } catch (error) {
      console.error('Error during OTP verification with backend:', error);
      alert('Failed to verify OTP. Please check your network and try again.');
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifyingOtp(false);
    }
  };

  const handleMaybeLater = () => {
    closePopup();
  };

  const handleDigitChange = (index, value) => {
    const newOtpDigits = [...otpDigits];
    if (/^[0-9]$/.test(value) || value === '') {
      newOtpDigits[index] = value;
      setOtpDigits(newOtpDigits);
      if (value && index < otpDigits.length - 1) {
        const nextInput = document.querySelectorAll(`.otp-digit-input`)[index + 1];
        if (nextInput) nextInput.focus();
      } else if (value && index === 5) {
        if (newOtpDigits.every((digit) => digit !== '')) {
          handleOtpSubmit(null, newOtpDigits.join(''));
        }
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const prevInput = document.querySelector(`.otp-digit-input:nth-of-type(${index})`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleResendOtp = () => {
    if (phone && name) {
      handleMobileSubmit({ preventDefault: () => { } });
      setOtpDigits(['', '', '', '', '', '']);
      setIsVerifyingOtp(false);
      verifyInFlightRef.current = false;
    } else {
      alert('Name and Mobile number are required to resend OTP. Please fill the form again.');
      setOtpSent(false);
      setUserIdToVerify('');
    }
  };

  // If neither internal show nor parent visible, do not render
  if (!isVisible) return null;

  // Styles (you can keep CSS file; inline styles here are fallback)
  const buttonStyle = {
    cursor: isFormValid ? 'pointer' : 'not-allowed',
    backgroundColor: isFormValid ? 'rgb(9, 156, 241)' : '#717171',
    color: 'white',
    padding: '10px 40px',
    borderRadius: '15px',
    border: isFormValid ? '1px solid rgb(9, 156, 241)' : '1px solid #717171',
    fontSize: '16px',
  };

  const otpButtonStyle = {
    ...buttonStyle,
    cursor: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? 'pointer' : 'not-allowed',
    backgroundColor: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? 'rgb(9, 156, 241)' : '#d3d3d3',
    border: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? '1px solid rgb(9, 156, 241)' : '1px solid #d3d3d3',
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <FormMessageDisplay message={formMessage.text} type={formMessage.type} />
        {!otpSent && (
          <div className="row">
            <p className="popup-subtitle">
              Get the list of best <span className="service-type">{serviceType}</span> service provider
            </p>
          </div>
        )}

        {!otpSent ? (
          <form onSubmit={handleMobileSubmit} className="horizontal-form">
            <div className="form-row">
              <div className="floating-label">
                <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                <label htmlFor="name" className={name ? 'filled' : ''}>
                  Enter Your Name
                </label>
              </div>
              <div className="floating-label">
                <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <label htmlFor="email" className={email ? 'filled' : ''}>
                  Enter Your Email (Optional)
                </label>
              </div>
            </div>

            <div className="floating-label">
              <PhoneInput
                country={'in'}
                enableSearch={true}
                countryCodeEditable={false}
                preferredCountries={['in', 'us', 'ae', 'gb', 'au']}
                value={phone}
                onChange={(value) => setPhone(value)}
                inputProps={{
                  type: 'tel',
                  name: 'mobile',
                  required: true,
                  autoFocus: true,
                  placeholder: 'Enter Your Mobile Number',
                }}
                inputStyle={{
                  width: '100%',
                  height: '45px',
                  fontSize: '16px',
                  paddingLeft: '48px',
                  borderRadius: '7px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box',
                }}
                buttonStyle={{
                  border: 'none',
                  background: 'transparent',
                }}
                dropdownStyle={{
                  maxHeight: '260px',
                  overflowY: 'auto',
                  zIndex: 1000,
                }}
                containerStyle={{
                  width: '100%',
                  padding: '0',
                }}
              />
            </div>

            <div className="button-container">
              <button type="submit" className="otp-button" disabled={!isFormValid} style={buttonStyle}>
                Login with OTP
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="otp-form">
            <h2 className="otp-verification">OTP Verification</h2>
            <p className="otp-paragraph">
              Please enter the OTP (One-Time Password) sent to your registered mobile number{' '}
              <span className="service-type">(+{phone})</span> to complete your verification.
            </p>

            <div className="otp-input-container">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  type="tel"
                  maxLength="1"
                  className="otp-digit-input"
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  required
                />
              ))}
            </div>

            <div className="otp-timer">Remaining time: {formatTime(remainingTime)}</div>
            <div className="otp-resend">
              Didn't get the code?{' '}
              {remainingTime > 0 ? (
                <span style={{ color: '#aaa', cursor: 'not-allowed' }}>Resend</span>
              ) : (
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    handleResendOtp();
                  }}
                  style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                >
                  Resend
                </a>
              )}
            </div>

            <div className="button-container otp-button-group">
              <button
                type="submit"
                className="otp-button verify-button"
                disabled={!isOtpFormValid || isVerifyingOtp || remainingTime <= 0}
                style={otpButtonStyle}
              >
                {isVerifyingOtp ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" className="otp-button cancel-button" onClick={handleMaybeLater}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="maybe-later-link">
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              handleMaybeLater();
            }}
          >
            Maybe Later
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPopup;
